import type { ShiftGeneratorResult } from '@api/ShiftGeneratorResultService';
import type { DaySegment, SpecialCode } from '@api/ShiftTypeService';
import type { WorkSegment } from '@api/PatternTemplateService';
import {
    getStateEntries,
    shiftsStringToShiftCode,
    type EmployeesWithEmploymentInfo,
} from '../transforming';
import { getNextDateString } from '../dates';

type DaySegmentsByShiftCodes = {
    [k: string]: DaySegment;
};

interface NightShiftAdjustmentOptions {
    isNightShiftOnNextDay: boolean;
    daySegmentsByShiftCodes?: DaySegmentsByShiftCodes | undefined;
}

export type EmployeeSchedule = {
    id: string; // Employee ID (table requires id prop)
    name?: string | undefined;
    positionName?: string;
    positionId?: string;
    vacancyRate?: number | null;
    [date: string]: string | number | undefined | null; // date keys like '2025-10-06'
};

/** Shifts night shift codes to next day. Therefore the resulting array is longer than the input. */
const getAdjustedShiftCodes = (
    shiftCodes: string[],
    daySegmentsByShiftCodes: DaySegmentsByShiftCodes,
): string[] => {
    const adjustedShiftCodes = [...shiftCodes];

    for (let i = 0; i < shiftCodes.length; i += 1) {
        const currentShiftCode = shiftCodes[i] as string;
        const prevShiftCode = i > 0 ? shiftCodes[i - 1] : undefined;

        if (
            daySegmentsByShiftCodes[currentShiftCode] === 'N' &&
            (prevShiftCode === undefined ||
                daySegmentsByShiftCodes[prevShiftCode] !== 'N')
        ) {
            adjustedShiftCodes[i] = 'F0';
        } else if (
            prevShiftCode !== undefined &&
            daySegmentsByShiftCodes[currentShiftCode] !== 'N' &&
            daySegmentsByShiftCodes[prevShiftCode] === 'N'
        ) {
            adjustedShiftCodes[i] = prevShiftCode;
        }
    }

    const lastShiftCode = shiftCodes[shiftCodes.length - 1];
    if (lastShiftCode !== undefined) {
        const lastDaySegment = daySegmentsByShiftCodes[lastShiftCode];
        if (lastDaySegment !== 'N') {
            adjustedShiftCodes.push('F0');
        } else {
            adjustedShiftCodes.push(lastShiftCode);
        }
    }

    return adjustedShiftCodes;
};

export const matchShiftCodesToDates = (
    days: string[], // list of all dates in the plan
    shifts: string,
    nightShiftAdjustmentOptions: NightShiftAdjustmentOptions,
): { [date: string]: string } => {
    let shiftCodes = shiftsStringToShiftCode(shifts);
    let relevantDays = days;

    const { isNightShiftOnNextDay, daySegmentsByShiftCodes } =
        nightShiftAdjustmentOptions;

    const shouldAdjustShifts = isNightShiftOnNextDay && daySegmentsByShiftCodes;

    const lastDay = days.at(-1);

    if (shouldAdjustShifts && lastDay) {
        shiftCodes = getAdjustedShiftCodes(shiftCodes, daySegmentsByShiftCodes);
        // Adjusting night shifts to be shown the next day requires extending the number of relevant days by one.
        relevantDays = [...days, getNextDateString(lastDay)];
    }

    const datesAndShiftCodes = Object.assign(
        {},
        ...shiftCodes.map((shiftCode, index) => ({
            // It is assumed that the number of days and number of shift codes match
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            [relevantDays[index]!]: shiftCode,
        })),
    );

    return datesAndShiftCodes;
};

export const getCorrespondingDatesAndShiftCodes = (
    shiftGeneratorResult: ShiftGeneratorResult,
    nightShiftAdjustmentOptions: NightShiftAdjustmentOptions,
    employeesWithEmploymentInfo?: EmployeesWithEmploymentInfo<
        string | undefined
    >,
): EmployeeSchedule[] => {
    const { days, state } = shiftGeneratorResult;
    if (!state || !days) {
        return [];
    }

    const employeeIds = Object.keys(employeesWithEmploymentInfo ?? {});

    return getStateEntries(state, employeeIds).map(([employeeId, shifts]) => {
        const datesAndShiftCodes = matchShiftCodesToDates(
            days,
            shifts,
            nightShiftAdjustmentOptions,
        );

        const employeeDatesAndShiftCodes = {
            id: employeeId,
            ...datesAndShiftCodes,
        };

        const employeeInfo = employeesWithEmploymentInfo?.[employeeId];

        return employeeInfo
            ? {
                  employeeName: employeeInfo.name,
                  positionName: employeeInfo.positionName,
                  positionId: employeeInfo.positionId,
                  vacancyRate: employeeInfo.vacancyRate,
                  ...employeeDatesAndShiftCodes,
              }
            : employeeDatesAndShiftCodes;
    });
};

export const DAY_SEGMENT_LABELS: Record<DaySegment, string> = {
    D: 'Dagvakt',
    A: 'Kveldsvakt',
    L: 'Langvakt',
    N: 'Nattevakt',
};

export const WORK_SEGMENT_LABELS: Record<WorkSegment, string> = {
    ...DAY_SEGMENT_LABELS,
    X: 'Jobber ikke',
};

export const SPECIAL_CODE_LABELS: Record<SpecialCode, string> = {
    FE: 'Ferie',
    F3: 'F3',
    P: 'Permisjon',
};

export const DAY_SEGMENT_SHORT_LABELS: Record<DaySegment, string> = {
    D: 'Dag',
    A: 'Kveld',
    L: 'Lang',
    N: 'Natt',
};
